import React, { useCallback, useState, useEffect, useMemo } from 'react';
import keyBy from 'lodash/keyBy';
import { R300 } from '@atlaskit/theme/colors';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import Form, { FormSection, FormFooter } from '@atlaskit/form';
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { Flex, Text, xcss, Stack, Inline } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { deactivateShipToParty } from '@atlassian/temp-proxies';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import {
  useNotification,
  useSidebar,
  successNotificationBuilder,
  errorNotificationBuilder,
  NotificationTypes,
  BillingProfileSelectItem,
} from '@css-xp/base';
import { ContactInfo } from '@css-xp/graphtypes/src/generated-types-new';
import { useFetchBillingProfilesForTransactionAccountQuery } from '@css-xp/ppc-billing-profile-table';
import { ContactType, useFetchEccFromContactService } from '@css-xp/contact-resources';
import { SwitchBillingProfileCards } from '@css-xp/base/forms/src/switch-billing-profile-cards';

interface Props {
  transactionAccountId: string;
  shipToId: string;
  onComplete?: (_responsePayload?: any) => void;
  isPrimaryContactsEnabled?: boolean;
  shouldShowACHPaymentMethod?: boolean;
}

export const DeactivateSoldToAddressPane = ({
  transactionAccountId,
  shipToId,
  onComplete,
  isPrimaryContactsEnabled = false,
  shouldShowACHPaymentMethod = false,
}: Props): JSX.Element => {
  const { getBulkECCDetails } = useFetchEccFromContactService(ContactType.CUSTOMER_CONTACT);
  const [isDeactivating, setIsDeactivating] = useState<boolean>(false);
  const [billingProfiles, setBillingProfiles] = useState<BillingProfileSelectItem[]>([]);
  const [loadingBillingProfiles, setLoadingBillingProfiles] = useState<boolean>(true);
  const { closeSidebar } = useSidebar();
  const { sendSuccessNotification, notificationSent, setNotificationSent, sendErrorNotification } = useNotification();

  const { data, loading: isBillingProfileDetailsLoading } = useFetchBillingProfilesForTransactionAccountQuery({
    soldToAddressIds: [shipToId],
    transactionAccountId: transactionAccountId,
    numberOfBillingProfilesToFetch: 100,
  });

  const activeBillingProfiles = useMemo(
    () =>
      data?.transactionAccount?.billingProfiles?.nodes
        ?.filter((node) => node?.isActive)
        ?.map((node) => ({
          invoiceGroup: node,
        })) ?? [],
    [data]
  );
  const activeBillingProfilesCount = activeBillingProfiles.length;

  const handleDeactivate = useCallback(async () => {
    try {
      setIsDeactivating(true);
      await deactivateShipToParty(shipToId, transactionAccountId);
      const notificationOptions = successNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withId('update-shipTo-party-success')
        .withHeader('Sold-to address deactivated')
        .withDismissAfter(5000)
        .build();
      setIsDeactivating(false);
      sendSuccessNotification(notificationOptions);
      setNotificationSent(true);
      closeSidebar();
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      if (!notificationSent) {
        const notificationOptions = errorNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader('Unable to deactivate sold-to address')
          .withMessage('Check your internet connection and try again')
          .withDismissAfter(5000)
          .withId('ship-to-party-failure')
          .build();
        setIsDeactivating(false);
        sendErrorNotification(notificationOptions);
        setNotificationSent(true);
        closeSidebar();
      }
    }
  }, [shipToId]);

  useEffect(() => {
    (async () => {
      if (activeBillingProfilesCount > 0 && transactionAccountId) {
        setLoadingBillingProfiles(true);

        const invoiceGroupIds = activeBillingProfiles.map((profile) => profile?.invoiceGroup?.id ?? '');
        let contactsByIG: Record<string, ContactInfo>;
        if (isPrimaryContactsEnabled) {
          let contacts = await getBulkECCDetails(invoiceGroupIds, transactionAccountId);
          contacts = contacts?.filter((contact: ContactInfo) => contact?.isPrimary);
          contactsByIG = keyBy(contacts, 'identifierId');
        }
        const updatedProfiles = activeBillingProfiles.map((profile) => ({
          invoiceGroup: {
            ...profile.invoiceGroup,
          },
          primaryCustomerContact: {
            data: isPrimaryContactsEnabled ? contactsByIG[profile?.invoiceGroup?.id ?? ''] : null,
          },
        }));

        setBillingProfiles(updatedProfiles as BillingProfileSelectItem[]);
        setLoadingBillingProfiles(false);
      }
    })();
  }, [activeBillingProfiles]);

  const getFormSection = (isBillingProfileDetailsLoading: boolean, activeBillingProfilesCount: number) => {
    if (isBillingProfileDetailsLoading) {
      return <LoadingIndicator />;
    }

    if (activeBillingProfilesCount > 0) {
      return (
        <>
          <Stack space="space.500">
            <Stack space="space.200">
              <Text weight="semibold">Billing profile check</Text>
              <Inline space="space.050">
                <ErrorIcon label="Error" size="medium" primaryColor={R300} />
                <Stack space="space.100">
                  <Text>Address is linked to {activeBillingProfilesCount} active billing profiles.</Text>
                  <Text>
                    Before deactivating, you must first change the address for each profile, or deactivate the linked
                    billing profiles from the “Billing profiles” tab.
                  </Text>
                </Stack>
              </Inline>
            </Stack>
            <Stack space="space.200">
              <Text weight="semibold">Linked billing profiles</Text>
              <SwitchBillingProfileCards
                billingProfiles={billingProfiles as BillingProfileSelectItem[]}
                usePrimaryContactFromBillingProfile={true}
                loadingBillingProfiles={loadingBillingProfiles}
                shouldShowACHPaymentMethod={shouldShowACHPaymentMethod}
              />
            </Stack>
          </Stack>
        </>
      );
    }

    return (
      <>
        <Text weight="semibold">Billing profile check</Text>
        <Flex alignItems="center" gap="space.100" xcss={xcss({ paddingTop: 'space.250' })}>
          <CheckCircleIcon label="" primaryColor={token('color.icon.success')} />
          <Text>No active billing profiles linked</Text>
        </Flex>
      </>
    );
  };

  const getFormFooter = (isBillingProfileDetailsLoading: boolean, activeBillingProfilesCount: number) => {
    if (isBillingProfileDetailsLoading || activeBillingProfilesCount > 0) {
      return null;
    }
    return (
      <Flex direction="column">
        <Flex direction="column" gap="space.100">
          <Text weight="semibold">Keep in mind</Text>{' '}
          <Text>When you deactivate an address, it'll no longer be available for billing.</Text>
        </Flex>
        <ButtonGroup>
          <Flex>
            <Button appearance="default" onClick={closeSidebar}>
              Cancel
            </Button>
          </Flex>
          <Flex>
            <LoadingButton appearance="primary" type="submit" testId="update-button" isLoading={isDeactivating}>
              Deactivate
            </LoadingButton>
          </Flex>
        </ButtonGroup>
      </Flex>
    );
  };
  return (
    <Form onSubmit={handleDeactivate}>
      {({ formProps }) => (
        <form {...formProps}>
          <FormSection>{getFormSection(isBillingProfileDetailsLoading, activeBillingProfilesCount)}</FormSection>
          <FormFooter align="end">
            {getFormFooter(isBillingProfileDetailsLoading, activeBillingProfilesCount)}
          </FormFooter>
        </form>
      )}
    </Form>
  );
};
